<template>
  <div>
    <!-- Sub-tab bar -->
    <div class="flex border-b mb-6">
      <button
        class="px-4 py-2 -mb-px font-semibold border-b-2"
        :class="subTab === 'lots' ? 'border-custom-yellow text-custom-blue' : 'text-gray-500 border-transparent'"
        @click="subTab = 'lots'"
      >QC Lots</button>
      <button
        class="px-4 py-2 -mb-px font-semibold border-b-2"
        :class="subTab === 'recent-results' ? 'border-custom-yellow text-custom-blue' : 'text-gray-500 border-transparent'"
        @click="subTab = 'recent-results'"
      >Recent Results</button>
    </div>

    <!-- ── QC LOTS ─────────────────────────────────────────── -->
    <div v-if="subTab === 'lots'">

      <!-- Add / Edit form -->
      <div v-if="showLotForm" class="mb-6 p-5 border border-gray-200 rounded-lg bg-gray-50">
        <h3 class="text-sm font-semibold text-custom-blue mb-4">
          {{ editingLot ? 'Edit QC Lot' : 'Add QC Lot' }}
        </h3>
        <div class="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Analyte *</label>
            <select v-model="lotForm.lab_analyte_id" class="w-full border border-gray-300 rounded p-2 text-sm bg-white" :disabled="!!editingLot">
              <option value="">Select analyte…</option>
              <optgroup v-for="disc in disciplineGroups" :key="disc.label" :label="disc.label">
                <option v-for="a in disc.analytes" :key="a.id" :value="a.id">{{ a.name }}</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Lot Number *</label>
            <input v-model="lotForm.lot_number" type="text" placeholder="e.g. LOT-2026-001"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Manufacturer</label>
            <input v-model="lotForm.manufacturer" type="text" placeholder="e.g. Bio-Rad"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Level *</label>
            <select v-model="lotForm.level" class="w-full border border-gray-300 rounded p-2 text-sm bg-white">
              <option value="">Select…</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Mean *</label>
            <input v-model.number="lotForm.mean" type="number" step="0.0001"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">SD *</label>
            <input v-model.number="lotForm.sd" type="number" step="0.0001" min="0.0001"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Unit</label>
            <input v-model="lotForm.unit" type="text" placeholder="e.g. mmol/L"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Expiry Date</label>
            <input v-model="lotForm.expiry_date" type="date"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
        </div>
        <div class="flex justify-end space-x-2 mt-4">
          <button @click="cancelLotForm" class="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded">Cancel</button>
          <button @click="submitLotForm" :disabled="lotSaving"
            class="px-4 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-50">
            {{ lotSaving ? 'Saving…' : (editingLot ? 'Update' : 'Add Lot') }}
          </button>
        </div>
      </div>

      <!-- Table section — hidden while form is open (change #4) -->
      <template v-if="!showLotForm">
        <div class="flex justify-between items-center mb-3">
          <h3 class="text-sm font-semibold text-gray-600">Registered QC Lots</h3>
          <button @click="openAddLotForm"
            class="px-4 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600">
            + Add QC Lot
          </button>
        </div>

        <div v-if="lotsLoading" class="flex justify-center py-10">
          <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-custom-yellow"></div>
        </div>

        <div v-else class="overflow-x-auto shadow-sm rounded-sm">
          <table class="w-full text-left text-gray-700 text-sm">
            <thead>
              <tr class="bg-gray-100">
                <th class="py-3 px-4">#</th>
                <th class="py-3 px-4">Analyte</th>
                <th class="py-3 px-4">Discipline</th>
                <th class="py-3 px-4">Lot Number</th>
                <th class="py-3 px-4">Level</th>
                <th class="py-3 px-4">Mean ± SD</th>
                <th class="py-3 px-4">Unit</th>
                <th class="py-3 px-4">Expiry</th>
                <th class="py-3 px-4">Status</th>
                <th class="py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody class="text-gray-600 font-light">
              <tr v-if="paginatedLots.length === 0">
                <td colspan="10" class="py-6 text-center text-gray-400">No QC lots registered yet.</td>
              </tr>
              <tr v-for="(lot, idx) in paginatedLots" :key="lot.id" class="border-b border-gray-200 hover:bg-gray-50">
                <td class="py-3 px-4">{{ (lotsPage - 1) * lotsPerPage + idx + 1 }}</td>
                <td class="py-3 px-4 font-medium">{{ lot.lab_analyte?.name }}</td>
                <td class="py-3 px-4">
                  <span class="px-2 py-0.5 rounded-full text-xs" :class="disciplineClass(lot.lab_analyte?.discipline)">
                    {{ lot.lab_analyte?.discipline }}
                  </span>
                </td>
                <td class="py-3 px-4">{{ lot.lot_number }}</td>
                <td class="py-3 px-4">
                  <span class="px-2 py-0.5 rounded-full text-xs" :class="levelClass(lot.level)">{{ lot.level }}</span>
                </td>
                <td class="py-3 px-4">{{ lot.mean }} ± {{ lot.sd }}</td>
                <td class="py-3 px-4">{{ lot.unit || '—' }}</td>
                <td class="py-3 px-4">{{ lot.expiry_date || '—' }}</td>
                <td class="py-3 px-4">
                  <span class="px-2 py-0.5 rounded-full text-xs"
                    :class="lot.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'">
                    {{ lot.active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <!-- change #1 + #3: View, Enter Result, Edit, Activate/Deactivate -->
                <td class="py-3 px-4 space-x-2 whitespace-nowrap">
                  <button @click="$router.push('/quality-control/lot/' + lot.id)"
                    class="text-xs text-custom-blue underline">View</button>
                  <button @click="openResultModal(lot)"
                    class="text-xs text-green-600 underline">Enter Result</button>
                  <button @click="openEditLotForm(lot)"
                    class="text-xs text-gray-500 underline">Edit</button>
                  <button @click="toggleLotActive(lot)" class="text-xs underline"
                    :class="lot.active ? 'text-red-500' : 'text-green-600'">
                    {{ lot.active ? 'Deactivate' : 'Activate' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <PaginationComponent
          v-model:currentPage="lotsPage"
          :totalPages="lotsTotalPages"
          :itemsPerPage="lotsPerPage"
          :totalItems="(qcLots || []).length"
          @update:currentPage="lotsPage = $event"
        />
      </template>
    </div>

    <!-- ── RECENT RESULTS (change #2) ────────────────────────── -->
    <div v-if="subTab === 'recent-results'">
      <div v-if="allResultsLoading" class="flex justify-center py-10">
        <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-custom-yellow"></div>
      </div>
      <div v-else class="overflow-x-auto shadow-sm rounded-sm">
        <table class="w-full text-left text-gray-700 text-sm">
          <thead>
            <tr class="bg-gray-100">
              <th class="py-3 px-4">#</th>
              <th class="py-3 px-4">Analyte</th>
              <th class="py-3 px-4">Level</th>
              <th class="py-3 px-4">Lot No.</th>
              <th class="py-3 px-4">Date</th>
              <th class="py-3 px-4">Shift</th>
              <th class="py-3 px-4">Value</th>
              <th class="py-3 px-4">z-score</th>
              <th class="py-3 px-4">Flags</th>
              <th class="py-3 px-4">Status</th>
            </tr>
          </thead>
          <tbody class="text-gray-600 font-light">
            <tr v-if="allResults.length === 0">
              <td colspan="10" class="py-6 text-center text-gray-400">No QC results recorded yet.</td>
            </tr>
            <tr v-for="(r, idx) in allResults" :key="r.id" class="border-b border-gray-200 hover:bg-gray-50">
              <td class="py-3 px-4">{{ idx + 1 }}</td>
              <td class="py-3 px-4 font-medium">{{ r.qc_lot?.lab_analyte?.name }}</td>
              <td class="py-3 px-4">
                <span class="px-2 py-0.5 rounded-full text-xs" :class="levelClass(r.qc_lot?.level)">
                  {{ r.qc_lot?.level }}
                </span>
              </td>
              <td class="py-3 px-4">{{ r.qc_lot?.lot_number }}</td>
              <td class="py-3 px-4">{{ r.run_date }}</td>
              <td class="py-3 px-4">{{ r.shift || '—' }}</td>
              <td class="py-3 px-4 font-medium">{{ r.value }}</td>
              <td class="py-3 px-4" :class="zScoreClass(zScore(r.value, r.qc_lot))">
                {{ zScore(r.value, r.qc_lot) }}
              </td>
              <td class="py-3 px-4">
                <span v-if="!r.westgard_flags?.length" class="text-gray-400 text-xs">—</span>
                <span v-for="flag in r.westgard_flags" :key="flag"
                  class="inline-block mr-1 px-2 py-0.5 rounded-full text-xs"
                  :class="flag === '1_2s' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'">
                  {{ flag }}
                </span>
              </td>
              <td class="py-3 px-4">
                <span class="px-2 py-0.5 rounded-full text-xs"
                  :class="r.accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                  {{ r.accepted ? 'Accepted' : 'Rejected' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ── ENTER RESULT MODAL (change #3) ─────────────────────── -->
    <div v-if="showResultModal"
      class="fixed inset-0 bg-gray-800 bg-opacity-60 overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div class="bg-white px-8 py-6 rounded-lg shadow-xl w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-bold text-custom-blue">Enter QC Result</h2>
          <button @click="closeResultModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 14 14" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
            </svg>
          </button>
        </div>

        <!-- Lot info card -->
        <div v-if="modalLot" class="mb-4 p-3 bg-blue-50 border border-blue-100 rounded text-xs text-gray-700 space-y-1">
          <div class="font-semibold text-custom-blue">{{ modalLot.lab_analyte?.name }} — {{ modalLot.level }}</div>
          <div>Lot: <strong>{{ modalLot.lot_number }}</strong></div>
          <div>Mean: <strong>{{ modalLot.mean }}</strong> {{ modalLot.unit }} &nbsp;|&nbsp; SD: <strong>{{ modalLot.sd }}</strong></div>
          <div>
            ±2SD: {{ (parseFloat(modalLot.mean) - 2 * parseFloat(modalLot.sd)).toFixed(4) }}
            – {{ (parseFloat(modalLot.mean) + 2 * parseFloat(modalLot.sd)).toFixed(4) }}
          </div>
        </div>

        <div class="space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Result Value *</label>
            <input v-model.number="modalForm.value" type="number" step="0.0001" placeholder="0.0000"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Run Date *</label>
            <input v-model="modalForm.run_date" type="date"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Shift</label>
            <select v-model="modalForm.shift" class="w-full border border-gray-300 rounded p-2 text-sm bg-white">
              <option value="">— none —</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="night">Night</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">Comment</label>
            <input v-model="modalForm.comment" type="text" placeholder="Optional note…"
              class="w-full border border-gray-300 rounded p-2 text-sm bg-white" />
          </div>

          <!-- Result feedback shown after save -->
          <div v-if="modalLastResult" class="p-3 rounded border text-sm"
            :class="modalLastResult.accepted ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'">
            <div class="font-semibold" :class="modalLastResult.accepted ? 'text-green-700' : 'text-red-700'">
              {{ modalLastResult.accepted ? 'Accepted' : 'Rejected' }}
              — z = {{ zScore(modalLastResult.value, modalLot) }}
            </div>
            <div v-if="modalLastResult.westgard_flags?.length" class="mt-1 flex flex-wrap gap-1">
              <span v-for="flag in modalLastResult.westgard_flags" :key="flag"
                class="px-2 py-0.5 rounded-full text-xs"
                :class="flag === '1_2s' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'">
                {{ flag }}
              </span>
            </div>
            <div v-else class="mt-1 text-xs text-green-600">No Westgard violations</div>
          </div>

          <div class="flex justify-end space-x-2 pt-2">
            <button @click="closeResultModal" class="px-4 py-2 text-sm bg-gray-200 text-gray-800 rounded">Close</button>
            <button @click="submitModalResult" :disabled="modalSaving"
              class="px-4 py-2 text-sm bg-custom-yellow text-white rounded hover:bg-orange-600 disabled:opacity-50">
              {{ modalSaving ? 'Saving…' : 'Record Result' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationComponent from '@/components/shared/pagination.vue'
import apiClient from '@/services/api'

export default {
  name: 'QualityControlTab',
  components: { PaginationComponent },
  props: {
    searchQuery: { type: String, default: '' },
  },

  data() {
    const today = new Date().toISOString().split('T')[0]
    return {
      subTab: 'lots',

      // Lots sub-tab
      qcLots:      [],
      analytes:    [],
      lotsLoading: false,
      lotsPage:    1,
      lotsPerPage: 10,
      showLotForm: false,
      editingLot:  null,
      lotSaving:   false,
      lotForm: {
        lab_analyte_id: '',
        lot_number:     '',
        manufacturer:   '',
        level:          '',
        mean:           '',
        sd:             '',
        unit:           '',
        expiry_date:    '',
      },

      // Recent Results sub-tab
      allResults:        [],
      allResultsLoading: false,

      // Enter Result modal
      showResultModal: false,
      modalLot:        null,
      modalSaving:     false,
      modalLastResult: null,
      modalForm: {
        qc_lot_id: '',
        value:     '',
        run_date:  today,
        shift:     '',
        comment:   '',
      },
    }
  },

  computed: {
    disciplineGroups() {
      const groups = {}
      this.analytes.forEach(a => {
        if (!groups[a.discipline]) groups[a.discipline] = []
        groups[a.discipline].push(a)
      })
      return Object.entries(groups).map(([label, analytes]) => ({ label, analytes }))
    },

    lotsTotalPages() {
      return Math.max(1, Math.ceil((this.qcLots || []).length / this.lotsPerPage))
    },

    paginatedLots() {
      const start = (this.lotsPage - 1) * this.lotsPerPage
      return (this.qcLots || []).slice(start, start + this.lotsPerPage)
    },
  },

  watch: {
    subTab(val) {
      if (val === 'recent-results') this.loadAllResults()
    },
  },

  mounted() {
    this.loadLots()
    this.loadAnalytes()
  },

  methods: {
    // ── Data loading ──────────────────────────────────────────
    async loadLots() {
      this.lotsLoading = true
      try {
        const res = await apiClient.listQcLots()
        if (res.success && Array.isArray(res.data)) {
          this.qcLots = res.data
        }
      } catch (e) {
        console.error('loadLots:', e)
      } finally {
        this.lotsLoading = false
      }
    },

    async loadAnalytes() {
      try {
        const res = await apiClient.listLabAnalytes()
        if (res.success) this.analytes = res.data
      } catch { /* silently ignore */ }
    },

    async loadAllResults() {
      this.allResultsLoading = true
      try {
        const res = await apiClient.listQcResults()
        if (res.success && Array.isArray(res.data)) {
          this.allResults = [...res.data].reverse()
        }
      } catch (e) {
        console.error('loadAllResults:', e)
      } finally {
        this.allResultsLoading = false
      }
    },

    // ── Lots form ─────────────────────────────────────────────
    openAddLotForm() {
      this.editingLot  = null
      this.lotForm     = { lab_analyte_id: '', lot_number: '', manufacturer: '', level: '', mean: '', sd: '', unit: '', expiry_date: '' }
      this.showLotForm = true
    },

    openEditLotForm(lot) {
      this.editingLot = lot
      this.lotForm = {
        lab_analyte_id: lot.lab_analyte_id,
        lot_number:     lot.lot_number,
        manufacturer:   lot.manufacturer || '',
        level:          lot.level,
        mean:           lot.mean,
        sd:             lot.sd,
        unit:           lot.unit || '',
        expiry_date:    lot.expiry_date || '',
      }
      this.showLotForm = true
    },

    cancelLotForm() {
      this.showLotForm = false
      this.editingLot  = null
    },

    async submitLotForm() {
      if (!this.lotForm.lab_analyte_id || !this.lotForm.lot_number || !this.lotForm.level || this.lotForm.mean === '' || this.lotForm.sd === '') {
        this.$toast.error('Please fill in all required fields')
        return
      }
      this.lotSaving = true
      try {
        let res
        if (this.editingLot) {
          res = await apiClient.updateQcLot(this.editingLot.id, this.lotForm)
        } else {
          res = await apiClient.createQcLot(this.lotForm)
        }
        if (res.success) {
          this.$toast.success(this.editingLot ? 'QC lot updated' : 'QC lot created')
          this.cancelLotForm()
        } else {
          this.$toast.error(res.message || 'Failed to save')
        }
      } catch (e) {
        this.$toast.error(e.message || 'Failed to save QC lot')
      } finally {
        this.lotSaving = false
        await this.loadLots()
      }
    },

    async toggleLotActive(lot) {
      try {
        const res = await apiClient.toggleQcLot(lot.id)
        if (res.success) {
          lot.active = !lot.active
          this.$toast.success(res.message)
        }
      } catch {
        this.$toast.error('Failed to toggle lot status')
      }
    },

    // ── Enter Result modal ────────────────────────────────────
    openResultModal(lot) {
      const today = new Date().toISOString().split('T')[0]
      this.modalLot        = lot
      this.modalLastResult = null
      this.modalForm       = { qc_lot_id: lot.id, value: '', run_date: today, shift: '', comment: '' }
      this.showResultModal = true
    },

    closeResultModal() {
      this.showResultModal = false
      this.modalLot        = null
      this.modalLastResult = null
    },

    async submitModalResult() {
      if (this.modalForm.value === '' || !this.modalForm.run_date) {
        this.$toast.error('Value and run date are required')
        return
      }
      this.modalSaving = true
      try {
        const res = await apiClient.createQcResult(this.modalForm)
        if (res.success) {
          this.modalLastResult  = res.data
          this.modalForm.value  = ''
          this.modalForm.comment = ''
          if (!res.data.accepted) {
            this.$toast.error('Result recorded — Westgard violation detected')
          } else if (res.data.westgard_flags?.includes('1_2s')) {
            this.$toast.warning('Result recorded — Warning: outside 2SD')
          } else {
            this.$toast.success('Result recorded — Accepted')
          }
        } else {
          this.$toast.error(res.message || 'Failed to save result')
        }
      } catch (e) {
        this.$toast.error(e.message || 'Failed to save result')
      } finally {
        this.modalSaving = false
      }
    },

    // ── Helpers ───────────────────────────────────────────────
    zScore(value, lot) {
      if (!lot || parseFloat(lot.sd) === 0) return '—'
      return ((parseFloat(value) - parseFloat(lot.mean)) / parseFloat(lot.sd)).toFixed(2)
    },

    zScoreClass(z) {
      if (z === '—') return 'text-gray-400'
      const abs = Math.abs(parseFloat(z))
      if (abs > 3) return 'font-bold text-red-600'
      if (abs > 2) return 'font-semibold text-orange-500'
      return 'text-gray-700'
    },

    levelClass(level) {
      switch (level) {
        case 'low':    return 'bg-blue-100 text-blue-800'
        case 'normal': return 'bg-green-100 text-green-800'
        case 'high':   return 'bg-red-100 text-red-800'
        default:       return 'bg-gray-100 text-gray-600'
      }
    },

    disciplineClass(disc) {
      const map = {
        haematology:        'bg-red-100 text-red-700',
        biochemistry:       'bg-yellow-100 text-yellow-800',
        immunoassay:        'bg-purple-100 text-purple-700',
        urinalysis:         'bg-blue-100 text-blue-700',
        stool_parasitology: 'bg-green-100 text-green-700',
        microbiology:       'bg-indigo-100 text-indigo-700',
        serology:           'bg-pink-100 text-pink-700',
      }
      return map[disc] || 'bg-gray-100 text-gray-600'
    },
  },
}
</script>

<style lang="css" scoped></style>
